import { compact, difference, get, union } from 'lodash/fp';
import { useMemo, VFC } from 'react';

import {
  ChangeValue,
  WorkflowVersionStepDiff,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateVisualVersionDiff/types';
import {
  NewValueLabel,
  PrevValueLabel,
  ValueDiffLabel,
} from 'dashboard/apps/InquiryTemplateEditor/ValueDiffLabel';
import { PropertyRow } from 'dashboard/apps/InquiryTemplateEditor/VersionDiffTable';
import { generateSelectableEvents } from 'dashboard/components/WorkflowControlPanel/StepConfigWait';
import { createInputStep } from 'dashboard/lib/workflows';
import { Trigger } from 'dashboard/models/trigger';
import {
  WorkflowStep,
  WorkflowStepWait,
  WorkflowWatchedEvent,
} from 'dashboard/models/workflow-version';
import { EVENT_NAME_TO_DESCRIPTION } from 'lib/constants/events';

interface WaitDifferProps {
  step: WorkflowStepWait;
  workflowStepDiff?: WorkflowVersionStepDiff;
  stepsConfig: WorkflowStep[];
  trigger: Trigger;
}

const WaitDiffer: VFC<WaitDifferProps> = ({ workflowStepDiff, step, trigger, stepsConfig }) => {
  const waitStepChanges = workflowStepDiff?.changes?.step || {};

  const selectables = useMemo(
    () =>
      generateSelectableEvents({
        inputStep: createInputStep({ trigger }),
        waitStep: step,
        stepsConfig,
      }),
    [trigger, step, stepsConfig],
  );

  const getSelectableLabel = (token: string) =>
    selectables.find((selectable) => selectable.stepToken === token)?.objectName || 'None';

  const baseObjectName = get(['watched-events', 'prev-value', '0', 'step_token'], waitStepChanges);
  const targetObjectName = get(['watched-events', 'value', '0', 'step_token'], waitStepChanges);

  const extractEventNames = (events: WorkflowWatchedEvent[]) =>
    compact(
      events.map((event: WorkflowWatchedEvent) =>
        event['event-name'] ? EVENT_NAME_TO_DESCRIPTION[event['event-name']] : undefined,
      ),
    );

  const generateWatchedEventsLabelValue = (waitStepChanges: ChangeValue) => {
    const targetLabels = extractEventNames(waitStepChanges['value']);
    const baseLabels = extractEventNames(waitStepChanges['prev-value']);

    const addedLabels = difference(targetLabels, baseLabels);
    const removedLabels = difference(baseLabels, targetLabels);
    const unionLabels = union(targetLabels, baseLabels);

    return (
      <>
        {unionLabels.map((label, index) => {
          const suffix = index !== unionLabels.length - 1 ? ', ' : '';
          switch (true) {
            case addedLabels.includes(label):
              return (
                <NewValueLabel>
                  {label}
                  {suffix}
                </NewValueLabel>
              );
            case removedLabels.includes(label):
              return (
                <PrevValueLabel>
                  {label}
                  {suffix}
                </PrevValueLabel>
              );
            default:
              return label + suffix;
          }
        })}
      </>
    );
  };

  return (
    <>
      <PropertyRow textKey='Step Type' value='Wait' />
      {step['watched-events'] && step['watched-events'].length > 0 && (
        <>
          <PropertyRow
            textKey='Target Object'
            value={
              baseObjectName !== targetObjectName ? (
                <ValueDiffLabel
                  valueDiff={{
                    added: getSelectableLabel(targetObjectName),
                    removed: getSelectableLabel(baseObjectName),
                  }}
                  fallbackValue='None'
                />
              ) : (
                getSelectableLabel(step['watched-events'][0]['event-object-step-token'])
              )
            }
          />

          <PropertyRow
            textKey='Events'
            value={
              waitStepChanges?.['watched-events']
                ? generateWatchedEventsLabelValue(waitStepChanges['watched-events'])
                : extractEventNames(step['watched-events'])
            }
          />
        </>
      )}
      <PropertyRow
        textKey={
          step['watched-events'] && step['watched-events'].length > 0
            ? 'Default resolution time'
            : 'Duration to delay'
        }
        value={
          waitStepChanges['wait-period-seconds'] ? (
            <ValueDiffLabel
              valueDiff={{
                added: waitStepChanges['wait-period-seconds'].value,
                removed: waitStepChanges['wait-period-seconds']['prev-value'],
              }}
              fallbackValue='None'
              vertical={true}
            />
          ) : (
            step['wait-period-seconds']
          )
        }
      />
    </>
  );
};

export default WaitDiffer;
